/*
  Ручная привязка дома: адрес как в выгрузке, полный адрес и основание управления.

  Вынесено из `CourtBuildings.jsx` 01.10.2026, когда в панель участка встал
  список домов без участка (`UnassignedList.jsx`) и компонент упёрся в предел
  размера. Ручная форма осталась для того, чего в списке нет или что система не
  различает сама: «ул. Ленина, 3-14» - дом 3 и квартира 14 либо дом 3-14.

  Два адреса, и путать их нельзя (решение владельца 01.10.2026):
  - «как в выгрузке» - по нему находятся квартиры дома, и другое написание с
    выгрузкой не совпадёт;
  - «полный» - тот, что печатается в заявлении. Нужен, когда в записи выгрузки
    нет населённого пункта; предпросмотр говорит об этом заранее, а сервер без
    него такой дом не примет.

  Число помещений под каждым домом стоит на экране не для отчётности. Адрес дома
  вводит человек, а помещения приходят из выгрузки биллинга, и «подошло 0» -
  единственный признак того, что дом записан не так, как его пишет выгрузка.
*/

import { useState } from 'react'

import { fieldErrors, invalidClass as invalid } from '../api/fieldErrors.js'
import { LockedForm } from './AccessContext.jsx'
import { asBody } from './CourtBuildingBasis.jsx'
import { Field, Notice } from './Common.jsx'

const EMPTY = { protocol_number: '', protocol_date: '', contract_date: '' }

/*
  `onAttach` бросает отказ, а не прячет его (ревью 30.10.2026, аудит 26.10.2026, причина 6):
  ошибка вставала плашкой над вкладками справочника, далеко от этой формы внизу страницы, и
  поля не отмечались. Теперь причина стоит под формой, а неверное поле отмечено рамкой и
  `aria-invalid` (текст под полем в строке с кнопкой сбил бы линию - правило 6г).
*/
export default function BuildingForm({ section, onAttach, onPreview }) {
  const [address, setAddress] = useState('')
  const [full, setFull] = useState('')
  const [preview, setPreview] = useState(null)
  const [busy, setBusy] = useState(false)
  const [basis, setBasis] = useState(EMPTY)
  const [error, setError] = useState(null)
  const problems = fieldErrors(error)

  async function check() {
    const typed = address.trim()
    setPreview(typed ? await onPreview(typed) : null)
  }

  async function attach(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onAttach(section.id, address.trim(), asBody(basis), {
        full_address: full.trim() || null,
      })
    } catch (failure) {
      setError(failure)
      return
    } finally {
      setBusy(false)
    }
    setAddress('')
    setFull('')
    setBasis(EMPTY)
    setPreview(null)
  }

  return (
    <>
      <h3 className="section__title section__title--spaced">Дом, которого нет в списке</h3>
      {/* `row` стоит на внутреннем блоке: поля лежат внутри `fieldset` замка, и на форме
          строка их уже не собирает (осмотр 28.10.2026). */}
      <LockedForm onSubmit={attach}>
        <div className="row">
          <Field label="Адрес дома как в выгрузке" width="l" className={invalid(problems.address)}>
            <input
              className="field__input"
              value={address}
              aria-invalid={problems.address ? 'true' : undefined}
              onChange={(event) => setAddress(event.target.value)}
              onBlur={check}
              placeholder="ул. Ленина, 3"
            />
          </Field>
          <Field label="Полный адрес дома" width="l" className={invalid(problems.full_address)}>
            <input
              className="field__input"
              value={full}
              aria-invalid={problems.full_address ? 'true' : undefined}
              onChange={(event) => setFull(event.target.value)}
              placeholder="г. Кемерово, ул. Ленина, д. 3"
            />
          </Field>
          <Field
            label="Протокол собрания №"
            width="xs"
            className={invalid(problems['management.protocol_number'])}
          >
            <input
              className="field__input"
              value={basis.protocol_number}
              aria-invalid={problems['management.protocol_number'] ? 'true' : undefined}
              onChange={(event) => setBasis({ ...basis, protocol_number: event.target.value })}
            />
          </Field>
          <Field label="Дата протокола" width="s" className={invalid(problems['management.protocol_date'])}>
            <input
              className="field__input"
              type="date"
              value={basis.protocol_date}
              aria-invalid={problems['management.protocol_date'] ? 'true' : undefined}
              onChange={(event) => setBasis({ ...basis, protocol_date: event.target.value })}
            />
          </Field>
          {/*
            Договор управления заведён справочно: с 23.09.2026 в заявление он не
            попадает (решение владельца по ответу юриста), поэтому поле осталось,
            а подпись говорит, что документ от него не зависит.
          */}
          <Field
            label="Дата договора управления"
            width="s"
            className={invalid(problems['management.contract_date'])}
          >
            <input
              className="field__input"
              type="date"
              value={basis.contract_date}
              aria-invalid={problems['management.contract_date'] ? 'true' : undefined}
              onChange={(event) => setBasis({ ...basis, contract_date: event.target.value })}
            />
          </Field>
          <button className="button" type="submit" disabled={busy || !address.trim()}>
            Привязать дом
          </button>
        </div>
      </LockedForm>
      <Notice error={error} />

      {/*
        Пояснение стоит ПОД строкой с кнопкой, а не под своим полем: внутри
        строки оно удлинило бы поле снизу и увело кнопку вниз (правило 6г).
      */}
      <PreviewHint preview={preview} hasFull={Boolean(full.trim())} />
      <div className="muted">
        Полный адрес нужен, когда в выгрузке нет населённого пункта: он печатается в заявлении
        вместо записи из выгрузки. Протокол общего собрания нужен обязательно: с него начинается
        заявление, и он же прикладывается к пакету. Дата договора управления хранится для справки.
      </div>
    </>
  )
}

function PreviewHint({ preview, hasFull }) {
  if (!preview) {
    return (
      <div className="muted">
        Адрес пишется так же, как в выгрузке, но без квартиры: «ул. Ленина, 3».
      </div>
    )
  }
  if (preview.taken_by) {
    return <Notice kind="warn">Этот дом уже привязан к участку: {preview.taken_by}.</Notice>
  }
  if (preview.premises_count === 0) {
    return (
      <Notice kind="warn">
        Под этот адрес не подошло ни одно помещение портфеля. Проверьте написание: Арбитр ЖКХ
        сравнивает его с адресами из выгрузки.
      </Notice>
    )
  }
  const missing = preview.missing ?? []
  return (
    <Notice kind={missing.length && !hasFull ? 'warn' : 'ok'}>
      Под этот адрес подойдёт помещений: {preview.premises_count}.
      {missing.length && !hasFull
        ? ` В адресе нет ${missing.join(' и ')} - впишите полный адрес дома.`
        : ''}
    </Notice>
  )
}
